import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import axios, { AxiosError } from 'axios'
import { IConversationData } from '../../../model/conversation.interface'
import {
  DEFAULT_SELECTED_OPTIONS,
  ICouponBundles,
  ICouponBundlesDetails,
  ICouponExtendInfo,
  IDeductionGracedInfo,
  IRemainingCreditsCount,
  IWeeklyLessonPackageWarning,
  IWeeklyLessonProgress,
  IWeeklyLessonProgressSummary,
} from '../../../model/credit.interface'
import apis from '../../../modules/apis'
import { getJwtToken } from '../../../modules/auth'
import {
  companyEducationsConversationQueryApi,
  companyEducationsOnlyAITutorQueryApi,
  companyEducationsQueryApi,
  couponBundleDetailsQueryApi,
  couponExtendInfoQueryApi,
  couponPurchaseInfoQueryApi,
  deductionGraceCreditsSummaryQueryApi,
  deductionGracedRecentlyUsed,
  multicampusAITutorQueryApi,
  multicampusOneOnOneProgressQueryApi,
  pointsQueryApi,
  remainingCreditsCountQueryApi,
  weeklyLessonPackageWarningQueryApi,
  weeklyLessonProgressQueryApi,
} from '../../react-query/queryApis'
import { getQueryPageMetaData } from '../../utils'
import { createAxiosTokenConfig, handleAxiosError } from '../axios-util'

export const useCompanyEducationsOnlyAITutor = (config = {}) => {
  const token = getJwtToken()

  return useQuery<CompanyEducationType[], AxiosError>({
    queryKey: companyEducationsOnlyAITutorQueryApi.getQueryKey(),
    queryFn: companyEducationsOnlyAITutorQueryApi.getQueryFn(),

    // onError: (e) => handleAxiosError(e),
    retry: false,
    enabled: !!token,
    refetchOnWindowFocus: true,
    ...config,
  })
}

export type CompanyEducationType = {
  coupons: number
  current_week: number
  duration: number
  end_days: number
  lessons_or_cancellations: []
  name: string
  period: '2022.09.01~2022.09.30'
  recommend_lesson_count: number
  remain_lesson: number
  unused_coupons: number
  normal_user_lessons: number
  regular_coupons: number
  free_coupons: number
  ratio: number
  recommend_ratio: number
  completion_rate: number
  recommend_lesson_count_per_week: number
}

export const useCompanyEducations = (config = {}) => {
  const token = getJwtToken()

  return useQuery<CompanyEducationType[], AxiosError>({
    queryKey: companyEducationsQueryApi.getQueryKey(),
    queryFn: companyEducationsQueryApi.getQueryFn(),

    // onError: (e) => handleAxiosError(e),
    retry: false,
    enabled: !!token,
    refetchOnWindowFocus: true,
    ...config,
  })
}

export const useCompanyEducationsConversation = (config = {}) => {
  const token = getJwtToken()

  return useQuery<IConversationData[], AxiosError>({
    queryKey: companyEducationsConversationQueryApi.getQueryKey(),
    queryFn: companyEducationsConversationQueryApi.getQueryFn(),

    // onError: (e) => handleAxiosError(e),
    retry: false,
    enabled: !!token,
    refetchOnWindowFocus: true,
    ...config,
  })
}

export const useMulcamAITutor = (config = {}) => {
  const token = getJwtToken()

  return useQuery<any, AxiosError>({
    queryKey: multicampusAITutorQueryApi.getQueryKey(),
    queryFn: multicampusAITutorQueryApi.getQueryFn(),

    retry: false,
    enabled: !!token,
    refetchOnWindowFocus: true,
    ...config,
  })
}

export const useMulcamOneOnOneProgress = (config = {}) => {
  const token = getJwtToken()

  return useQuery<any, AxiosError>({
    queryKey: multicampusOneOnOneProgressQueryApi.getQueryKey(),
    queryFn: multicampusOneOnOneProgressQueryApi.getQueryFn(),

    retry: false,
    enabled: !!token,
    refetchOnWindowFocus: true,
    ...config,
  })
}

export const useRemainingCreditsCount = (option = {}) => {
  const token = getJwtToken()

  return useQuery<IRemainingCreditsCount, AxiosError>({
    queryKey: remainingCreditsCountQueryApi.getQueryKey(),
    queryFn: remainingCreditsCountQueryApi.getQueryFn(),

    // onError: (e) => handleAxiosError(e),
    retry: false,
    enabled: !!token,
    ...option,
  })
}

// 유저 보유한 수업권 중 Duration
export const useUserCreditDuration = (option = {}) => {
  const token = getJwtToken()

  return useQuery<IRemainingCreditsCount, AxiosError, number>({
    queryKey: remainingCreditsCountQueryApi.getQueryKey(),
    queryFn: remainingCreditsCountQueryApi.getQueryFn(),
    select: (data) => {
      const creditCount40 =
        data.regular_40_credit_count +
        data.makeup_40_credit_count +
        data.timelimit_40_credit_count +
        data.urgent_40_credit_count
      const creditCount20 =
        data.regular_20_credit_count +
        data.makeup_20_credit_count +
        data.timelimit_20_credit_count +
        data.urgent_20_credit_count

      let selectedDuration = null

      if (creditCount20 + creditCount40 > 0) {
        if (creditCount20 > 0 && creditCount40 == 0) {
          selectedDuration = 20
        } else if (creditCount40 > 0 && creditCount20 == 0) {
          selectedDuration = 40
        } else {
          selectedDuration = 40
        }
      } else {
        selectedDuration = 40
      }
      return selectedDuration
    },
    // onError: (e) => handleAxiosError(e),
    retry: false,
    enabled: !!token,
    ...option,
  })
}

interface UserCreditSummary {
  recommendedDuration: number
  creditCount20: number
  creditCount40: number
}

export const useUserCreditSummary = (option = {}) => {
  const token = getJwtToken()

  return useQuery<IRemainingCreditsCount, AxiosError, UserCreditSummary>({
    queryKey: remainingCreditsCountQueryApi.getQueryKey(),
    queryFn: remainingCreditsCountQueryApi.getQueryFn(),
    select: (data) => {
      const creditCount40 =
        data.regular_40_credit_count +
        data.makeup_40_credit_count +
        data.timelimit_40_credit_count +
        data.urgent_40_credit_count

      const creditCount20 =
        data.regular_20_credit_count +
        data.makeup_20_credit_count +
        data.timelimit_20_credit_count +
        data.urgent_20_credit_count

      let recommendedDuration = 40

      if (creditCount20 + creditCount40 > 0) {
        if (creditCount20 > 0 && creditCount40 === 0) {
          recommendedDuration = 20
        } else {
          recommendedDuration = 40
        }
      }

      return {
        recommendedDuration,
        creditCount20,
        creditCount40,
      }
    },
    retry: false,
    enabled: !!token,
    ...option,
  })
}

export const usePoints = () => {
  const token = getJwtToken()

  return useQuery<any, AxiosError>({
    queryKey: pointsQueryApi.getQueryKey(),
    queryFn: pointsQueryApi.getQueryFn(),

    // onError: (e) => handleAxiosError(e),
    retry: false,
    enabled: !!token,
  })
}

export const useHasRepurchasePoint = () => {
  const token = getJwtToken()

  return useQuery<any, AxiosError>({
    queryKey: pointsQueryApi.getQueryKey(),
    queryFn: pointsQueryApi.getQueryFn(),
    retry: false,
    enabled: !!token,
    select: (data) => {
      const { point_statement } = data
      const point = point_statement.find((point) => {
        return point.admin_can_expire && (point.content == '링글 응원 포인트' || point.content == 'Ringle boost points')
      })

      return point
    },
  })
}

export interface ICouponBundlesApiResponse {
  page: number
  total_count: number
  timelimit_filter: boolean
  urgent_filter: boolean
  coupon_bundles: ICouponBundles[]
}

export const getCurCreditsCnt = (pages: ICouponBundlesApiResponse[]) =>
  pages.map((page) => page.coupon_bundles).flat().length

export const useCouponBundlesList = ({ selectedFilters }) => {
  const token = getJwtToken()

  if (!selectedFilters.length) {
    selectedFilters = DEFAULT_SELECTED_OPTIONS
  }
  const queryKey = [apis.credit.couponBundles()].concat(selectedFilters)

  const queryParams = selectedFilters?.reduce((acc, curr) => {
    acc[curr.value] = 1
    return acc
  }, {})

  const fetchCouponBundles = async ({ pageParam = 0 }: { pageParam?: number }) => {
    const { limit, offset } = getQueryPageMetaData(pageParam)
    const response = await axios.get(apis.credit.couponBundles(), {
      ...createAxiosTokenConfig(token),
      params: {
        ...queryParams,
        limit,
        offset,
      },
    })

    return {
      ...response.data,
      page: pageParam,
    }
  }

  return useInfiniteQuery<ICouponBundlesApiResponse>({
    queryKey,
    queryFn: fetchCouponBundles,
    getNextPageParam: ({ page, total_count }, allPages) => {
      const nextPage = page + 1
      const curTotal = getCurCreditsCnt(allPages)
      return total_count - curTotal > 0 && nextPage
    },
    onError: (e: AxiosError) => handleAxiosError(e),
  })
}

export const useAITutorCouponBundlesList = () => {
  const token = getJwtToken()

  const queryKey = [apis.credit.couponBundlesAITutor()]

  const fetchCouponBundles = async ({ pageParam = 0 }: { pageParam?: number }) => {
    const { limit, offset } = getQueryPageMetaData(pageParam)
    const response = await axios.get(apis.credit.couponBundlesAITutor(), {
      ...createAxiosTokenConfig(token),
      params: {
        limit,
        offset,
      },
    })

    return {
      ...response.data,
      page: pageParam,
    }
  }

  return useInfiniteQuery<any>({
    queryKey,
    queryFn: fetchCouponBundles,
    getNextPageParam: ({ page, total_count }, allPages) => {
      const nextPage = page + 1
      const curTotal = getCurCreditsCnt(allPages)
      return total_count - curTotal > 0 && nextPage
    },
    onError: (e: AxiosError) => handleAxiosError(e),
  })
}

export const useCouponBundlesDetails = ({ bundleId }: { bundleId: number }) => {
  const token = getJwtToken()

  const pathParams = { bundleId }

  return useQuery<{ credit_info: ICouponBundlesDetails }, AxiosError>({
    queryKey: couponBundleDetailsQueryApi.getQueryKey({ pathParams }),
    queryFn: couponBundleDetailsQueryApi.getQueryFn({ pathParams }),

    // onError: (e) => handleAxiosError(e),
    retry: false,
    enabled: !!token,
  })
}

export const useCouponExtendInfo = ({ bundleId }: { bundleId: number | null }) => {
  const token = getJwtToken()

  const pathParams = { bundleId }

  return useQuery<ICouponExtendInfo, AxiosError>({
    queryKey: couponExtendInfoQueryApi.getQueryKey({ pathParams }),
    queryFn: couponExtendInfoQueryApi.getQueryFn({ pathParams }),

    // onError: (e) => handleAxiosError(e),
    retry: false,
    enabled: !!token && !!bundleId,
  })
}

export const usePurchaseInfo = ({ bundleId }: { bundleId: number | null }) => {
  const token = getJwtToken()

  const pathParams = { bundleId }

  return useQuery<ICouponExtendInfo, AxiosError>({
    queryKey: couponPurchaseInfoQueryApi.getQueryKey({ pathParams }),
    queryFn: couponPurchaseInfoQueryApi.getQueryFn({ pathParams }),

    // onError: (e) => handleAxiosError(e),
    retry: false,
    enabled: !!token && !!bundleId,
  })
}

export const useDeductionGracedRecentlyUsed = () => {
  const token = getJwtToken()
  return useQuery<IDeductionGracedInfo, AxiosError>({
    queryKey: deductionGracedRecentlyUsed.getQueryKey(),
    queryFn: deductionGracedRecentlyUsed.getQueryFn(),
    retry: false,
    enabled: !!token,
  })
}
export const useDeductionGraceCreditsSummary = ({ bundleId }: { bundleId: number | null }) => {
  const token = getJwtToken()

  const pathParams = { bundleId }

  return useQuery<IWeeklyLessonProgressSummary, AxiosError>({
    queryKey: deductionGraceCreditsSummaryQueryApi.getQueryKey({ pathParams }),
    queryFn: deductionGraceCreditsSummaryQueryApi.getQueryFn({ pathParams }),

    // onError: (e) => handleAxiosError(e),
    retry: false,
    enabled: !!token && !!bundleId,
  })
}

export const useWeeklyLessonProgress = ({ bundleId }: { bundleId: number | null }) => {
  const token = getJwtToken()

  const pathParams = { bundleId }

  return useQuery<IWeeklyLessonProgress, AxiosError>({
    queryKey: weeklyLessonProgressQueryApi.getQueryKey({ pathParams }),
    queryFn: weeklyLessonProgressQueryApi.getQueryFn({ pathParams }),

    // onError: (e) => handleAxiosError(e),
    retry: false,
    enabled: !!token && !!bundleId,
  })
}

export const useWeeklyLessonPackageWarning = () => {
  const token = getJwtToken()

  return useQuery<IWeeklyLessonPackageWarning, AxiosError>({
    queryKey: weeklyLessonPackageWarningQueryApi.getQueryKey(),
    queryFn: weeklyLessonPackageWarningQueryApi.getQueryFn(),

    // onError: (e) => handleAxiosError(e),
    retry: false,
    enabled: !!token,
  })
}
